export default function Introduction() {
  return (
    <div className="prose prose-blue max-w-none">
      <h1>Welcome to redgeGate API Documentation</h1>

      <p className="text-xl text-gray-600 mt-4">
        redgeGate is a powerful SMS gateway API that allows you to send and receive SMS messages programmatically using your Android devices.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8 not-prose">
        <div className="border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <h3 className="font-semibold text-gray-900 mb-2">Quick Start</h3>
          <p className="text-gray-600 text-sm">Get up and running in minutes with our step-by-step guide.</p>
        </div>

        <div className="border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
          <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="font-semibold text-gray-900 mb-2">API Reference</h3>
          <p className="text-gray-600 text-sm">Complete API documentation with examples in 8+ languages.</p>
        </div>

        <div className="border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
          <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
            <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
          <h3 className="font-semibold text-gray-900 mb-2">Guides</h3>
          <p className="text-gray-600 text-sm">Best practices, error handling, and advanced usage patterns.</p>
        </div>
      </div>

      <h2 className="mt-12">What is redgeGate?</h2>
      <p>
        redgeGate transforms your Android devices into powerful SMS gateways. Unlike traditional SMS APIs that require
        expensive carrier integrations, redgeGate uses your existing Android devices to send and receive messages.
      </p>

      <h3>Key Features</h3>
      <ul>
        <li><strong>Device-Based SMS</strong> - Use your Android devices as SMS gateways</li>
        <li><strong>RESTful API</strong> - Simple HTTP API with comprehensive documentation</li>
        <li><strong>Bulk Messaging</strong> - Send messages to multiple recipients efficiently</li>
        <li><strong>Real-time Webhooks</strong> - Get instant notifications for message events</li>
        <li><strong>Message History</strong> - Track all sent and received messages</li>
        <li><strong>Device Management</strong> - Monitor and control multiple devices</li>
        <li><strong>Secure Authentication</strong> - API key-based authentication with rate limiting</li>
      </ul>

      <h2>Authentication</h2>
      <p>
        All API requests require authentication using an API key. Include your API key in the <code>X-API-Key</code> header:
      </p>

      <div className="bg-gray-900 text-gray-100 rounded-lg p-4 mt-4 overflow-x-auto">
        <pre className="text-sm">
{`curl https://api.textredge.com/api/v1/sms/send \\
  -H "X-API-Key: your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "to": "+1234567890",
    "message": "Hello from redgeGate!"
  }'`}
        </pre>
      </div>

      <div className="bg-blue-50 border-l-4 border-blue-600 p-4 mt-6">
        <p className="text-sm text-blue-900">
          <strong>Note:</strong> You can generate and manage your API keys from the <a href="/dashboard/settings" className="underline">Dashboard Settings</a> page.
        </p>
      </div>

      <h2>Base URL</h2>
      <p>
        All API endpoints are relative to the base URL:
      </p>
      <div className="bg-gray-100 rounded-lg p-4 mt-2">
        <code className="text-blue-600">https://api.textredge.com/api/v1</code>
      </div>

      <h2>Rate Limits</h2>
      <p>
        API requests are rate-limited to ensure fair usage and system stability:
      </p>
      <ul>
        <li><strong>Free Tier:</strong> 100 requests per hour</li>
        <li><strong>Pro Tier:</strong> 1,000 requests per hour</li>
        <li><strong>Enterprise:</strong> Custom limits available</li>
      </ul>

      <p>
        Rate limit information is included in response headers:
      </p>
      <div className="bg-gray-900 text-gray-100 rounded-lg p-4 mt-4">
        <pre className="text-sm">
{`X-RateLimit-Limit: 100
X-RateLimit-Remaining: 95
X-RateLimit-Reset: 1696435200`}
        </pre>
      </div>

      <h2>Need Help?</h2>
      <p>
        If you have questions or need assistance:
      </p>
      <ul>
        <li>Check out our <a href="/docs/guides/errors">Error Handling Guide</a></li>
        <li>Browse the <a href="/docs/api/send-sms">API Reference</a></li>
        <li>Contact support at support@textredge.com</li>
      </ul>
    </div>
  )
}
